import { useLocation, useSearchParams } from "react-router-dom";

import { DefaultState, Location, SearchParam } from "@/shared/domain/navigation";

interface RouterHook<State> extends Omit<Location<State & DefaultState>, "search"> {
    globalState: {
        officeId: string | null;
        lang: string | null;
    };
    search: {
        [x: string | SearchParam]: string;
    };
    searchRaw: string;
    getStateOrSearch: <T extends keyof State & string>(key: T) => State[T] | string | null;
}

const tmpCleanKey = (key: string) => {
    return key.replace("??", "");
};

export const useRouterHook = <State>(): RouterHook<State> => {
    const { state, search: searchRaw, ...others } = useLocation() as Location<State & DefaultState>;
    const [searchParams] = useSearchParams(others.pathname);

    const search = {};

    for (const key of searchParams.keys()) {
        search[tmpCleanKey(key)] = searchParams.get(key);
    }

    const globalState = {
        officeId: state?.officeId || search[SearchParam.OfficeId] || null,
        lang: search[SearchParam.Language] || null,
    };

    const getStateOrSearch = <T extends keyof State & string>(key: T): State[T] | string | null => {
        const stateValue = state?.[key];
        const searchValue = searchParams.get(key);
        return stateValue || searchValue || null;
    };

    return {
        globalState,
        search,
        state: state || ({} as State & DefaultState),
        searchRaw,
        getStateOrSearch,
        ...others,
    };
};
